import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnChanges, ViewChild } from "@angular/core";
import * as d3 from "d3";

import { ColorsModel } from "../../colors";

const DEGREE = Math.PI / 180;
const ANGLE = DEGREE * 90;

@Component({
	selector: "pv-speedometer",
	template: "<div #speedometer></div>",
	styleUrls: ["./speedometer.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SpeedOMeterComponent implements OnChanges {
	private host = inject(ElementRef);
	private colorsModel = inject(ColorsModel);

	@Input() outerData: number;
	@Input() innerData: number | undefined;
	@Input() arcWidth = 12;

	@ViewChild("speedometer", { static: true }) speedometer: ElementRef;

	addCurve({
		value,
		index,
		width,
		data,
		path,
	}: {
		value: number;
		index: number;
		width: number;
		data: number[];
		path: any;
	}) {
		const colors = this.colorsModel.getSpeedOMeterColors();
		const radius = this.getRadius(index, width, value);

		const pathArc = d3.arc().innerRadius(radius.inner).outerRadius(radius.outer).startAngle(-ANGLE).endAngle(0);

		return d3
			.select(path)
			.style("fill", colors[index])
			.attr("d", pathArc.endAngle((data[index] - 0.5) * 180 * DEGREE))
			.attr("class", `arc${index}`);
	}

	getRadius(index: number, width: number, value: number) {
		const radius = {
			inner: value,
			outer: value,
		};

		if (index === 0) {
			radius.inner = width - this.arcWidth;
			radius.outer = width;
		} else {
			radius.inner = width - this.arcWidth * 2;
			radius.outer = width - this.arcWidth;
		}

		return radius;
	}

	loadMeter() {
		const width = this.host.nativeElement.clientWidth / 2;
		const height = this.host.nativeElement.clientHeight / 2;
		const data = [this.outerData, 1];

		if (this.innerData) {
			data.push(this.innerData);
		}

		if (this.speedometer.nativeElement.innerHTML) {
			this.speedometer.nativeElement.innerHTML = "";
		}

		// Append SVG to the body
		const svg = d3
			.select(this.speedometer.nativeElement)
			.append("svg")
			.attr("width", width * 2)
			.attr("height", height * 2);

		// Create background container for graph
		const background = d3
			.arc()
			.innerRadius(width - data.length * this.arcWidth)
			.outerRadius(width)
			.startAngle(-ANGLE)
			.endAngle(ANGLE);

		svg
			.append("path")
			.attr("d", background)
			.attr("fill", "transparent")
			.attr("transform", `translate(${width},${height})`);

		// Create container for curves
		const curves = svg.append("g").attr("transform", `translate(${width},${height * 2})`);

		// Append outerData to SpeedOMeter
		curves
			.selectAll("path")
			.data(data)
			.enter()
			.append("path")
			.each((value, index, paths) =>
				this.addCurve({
					value,
					index,
					width,
					data,
					path: paths[index],
				}),
			);

		svg.exit().remove();
	}

	ngOnChanges(changes) {
		if (!changes.outerData && !changes.innerData && !changes.arcWidth) {
			return;
		}

		this.loadMeter();
	}
}
